import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { UploadCloud, FileSpreadsheet, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function ImportSOA() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [mode, setMode] = useState("auto"); // 'auto' | 'manual'
    const [customers, setCustomers] = useState([]);
    const [customerId, setCustomerId] = useState("");
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [purgeFirst, setPurgeFirst] = useState(false);

    useEffect(() => {
        api.get("/customers").then((r) => setCustomers(r.data));
    }, []);

    async function submit(e, dryRun = false) {
        if (e) e.preventDefault();
        if (!file) {
            toast.error("Dosya seçin");
            return;
        }
        if (mode === "manual" && !customerId) {
            toast.error("Müşteri seçin");
            return;
        }
        // Admin-only purge confirmation
        if (mode === "auto" && !dryRun && purgeFirst) {
            const ok = window.confirm(
                "DİKKAT: Tüm mevcut fatura ve tahsilat kayıtları silinecek, ardından yeni SOA yüklenecek. Devam edilsin mi?"
            );
            if (!ok) return;
        }
        setUploading(true);
        if (!dryRun) setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            let url;
            if (mode === "auto") {
                const params = new URLSearchParams();
                if (dryRun) params.set("dry_run", "true");
                if (purgeFirst && !dryRun) params.set("purge_first", "true");
                const qs = params.toString();
                url = `/import/soa-auto${qs ? "?" + qs : ""}`;
            } else {
                url = `/import/soa?customer_id=${customerId}`;
            }
            const r = await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } });
            setResult({ mode, dry_run: dryRun, ...r.data });
            toast.success(dryRun ? "Önizleme hazır" : "İçe aktarma tamamlandı");
        } catch (err) {
            toast.error(formatApiError(err?.response?.data?.detail));
        } finally {
            setUploading(false);
        }
    }

    return (
        <div>
            <header className="border-b border-black/10 px-10 py-6">
                <div className="overline">Bölüm 03</div>
                <h1 className="font-display text-4xl font-black tracking-tight leading-none mt-1">SOA İçe Aktar</h1>
                <p className="text-sm text-neutral-500 mt-3 max-w-3xl">
                    <strong>Otomatik mod</strong>: Standart cari hesap ekstresi (HESAP KODU, ADI, TARIH, TURU, FISNO,
                    FATURANO, VADE, ACIKLAMA, BORC, ALACAK, BAKIYE BORC/ALACAK). Müşteriler HESAP KODU'na göre otomatik
                    bulunur/oluşturulur. S.F.→fatura, İ.F.→iade tahsilatı, GELEN HAVALE→banka tahsilatı, NAKLİ YEKUN→açılış bakiyesi olarak eşleştirilir. Vade tarihi otomatik alınır.
                </p>
            </header>

            <div className="px-10 py-6 border-b border-black/10 flex gap-2" data-testid="import-mode">
                <button
                    data-testid="import-mode-auto"
                    type="button"
                    onClick={() => setMode("auto")}
                    className={`border px-4 py-2 text-xs font-mono uppercase tracking-widest ${
                        mode === "auto" ? "bg-brand-ink text-white border-brand-ink" : "border-black/20"
                    }`}
                >
                    Otomatik (Standart Format)
                </button>
                <button
                    data-testid="import-mode-manual"
                    type="button"
                    onClick={() => setMode("manual")}
                    className={`border px-4 py-2 text-xs font-mono uppercase tracking-widest ${
                        mode === "manual" ? "bg-brand-ink text-white border-brand-ink" : "border-black/20"
                    }`}
                >
                    Manuel (Müşteri Seç)
                </button>
            </div>

            <div className="px-10 py-8 grid grid-cols-1 lg:grid-cols-5 gap-8">
                <form onSubmit={submit} className="lg:col-span-3 border border-black/15 p-6 space-y-5">
                    {mode === "manual" && (
                        <label className="block">
                            <span className="overline block mb-2">Müşteri Seç *</span>
                            <select
                                data-testid="import-customer"
                                required
                                value={customerId}
                                onChange={(e) => setCustomerId(e.target.value)}
                                className="w-full border border-black/20 px-3 py-2 text-sm font-mono bg-white focus:outline-none focus:border-brand-blue"
                            >
                                <option value="">— Seçiniz —</option>
                                {customers.map((c) => (
                                    <option key={c.id} value={c.id}>
                                        {c.name} {c.code ? `(${c.code})` : ""}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}

                    <label className="block">
                        <span className="overline block mb-2">Dosya *</span>
                        <div className="border border-dashed border-black/25 p-8 text-center bg-neutral-50">
                            <FileSpreadsheet className="mx-auto text-neutral-500" size={28} />
                            <input
                                data-testid="import-file"
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                onChange={(e) => setFile(e.target.files?.[0] || null)}
                                className="block mx-auto mt-4 text-xs font-mono"
                            />
                            {file && (
                                <div className="text-xs font-mono text-neutral-600 mt-2">Seçildi: {file.name}</div>
                            )}
                        </div>
                    </label>

                    <div className="flex gap-2">
                        {mode === "auto" && (
                            <button
                                type="button"
                                data-testid="import-preview"
                                disabled={uploading}
                                onClick={() => submit(null, true)}
                                className="btn-outline flex-1 justify-center"
                            >
                                {uploading ? "…" : "Önizleme"}
                            </button>
                        )}
                        <button data-testid="import-submit" disabled={uploading} className="btn-sharp flex-1 justify-center">
                            <UploadCloud size={14} />
                            {uploading ? "Yükleniyor…" : "İçe Aktar"}
                        </button>
                    </div>

                    {mode === "auto" && isAdmin && (
                        <label
                            data-testid="purge-first-toggle"
                            className={`flex items-start gap-3 p-3 border cursor-pointer transition ${
                                purgeFirst ? "border-brand-red bg-red-50" : "border-black/15 bg-neutral-50"
                            }`}
                        >
                            <input
                                type="checkbox"
                                checked={purgeFirst}
                                onChange={(e) => setPurgeFirst(e.target.checked)}
                                className="mt-1 accent-brand-red"
                                data-testid="purge-first-checkbox"
                            />
                            <div>
                                <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest">
                                    <AlertTriangle size={14} className="text-brand-red" />
                                    <span>Önceki SOA verilerini sil</span>
                                </div>
                                <p className="text-[11px] text-neutral-600 mt-1 leading-relaxed">
                                    İşaretlerseniz yükleme öncesi <strong>tüm mevcut fatura ve tahsilat kayıtları</strong> silinir.
                                    Bu işlem sadece yöneticiye açıktır ve geri alınamaz.
                                </p>
                            </div>
                        </label>
                    )}
                </form>

                <div className="lg:col-span-2">
                    {result && (
                        <div data-testid="import-result" className="border border-black/15 p-4 mb-6">
                            <div className="overline mb-2">
                                {result.dry_run ? "Önizleme (Kaydedilmedi)" : "Sonuç"}
                            </div>
                            {result.purged && (
                                <div className="mb-2 text-[11px] font-mono text-brand-red border border-brand-red/40 bg-red-50 px-2 py-1">
                                    ⚠ Önceki tüm fatura ve tahsilat kayıtları silindi
                                </div>
                            )}
                            {result.customers_created !== undefined && (
                                <>
                                    <div className="text-sm">
                                        Yeni müşteri: <span className="font-mono-num font-semibold">{result.customers_created}</span>
                                    </div>
                                    <div className="text-sm">
                                        Etkilenen müşteri: <span className="font-mono-num font-semibold">{result.customers_touched}</span>
                                    </div>
                                    <div className="text-sm">
                                        Açılış bakiyesi: <span className="font-mono-num font-semibold">{result.opening_balances_set}</span>
                                    </div>
                                </>
                            )}
                            <div className="text-sm">
                                Fatura: <span className="font-mono-num font-semibold">{result.invoices_added}</span>
                                {result.invoices_skipped ? (
                                    <span className="text-brand-amber ml-2 text-xs">
                                        · {result.invoices_skipped} mükerrer atlandı
                                    </span>
                                ) : null}
                            </div>
                            <div className="text-sm">
                                Tahsilat: <span className="font-mono-num font-semibold text-brand-green">{result.collections_added}</span>
                                {result.collections_skipped ? (
                                    <span className="text-brand-amber ml-2 text-xs">
                                        · {result.collections_skipped} mükerrer atlandı
                                    </span>
                                ) : null}
                            </div>
                            {result.preview?.length > 0 && (
                                <details className="mt-3" open>
                                    <summary className="overline cursor-pointer">Detay ({result.preview.length})</summary>
                                    <div className="max-h-64 overflow-y-auto mt-2 text-[11px] font-mono border border-black/10">
                                        {result.preview.map((p, i) => (
                                            <div
                                                key={i}
                                                className={`px-2 py-1 border-b border-black/5 flex justify-between gap-2 ${
                                                    p.kind === "skip_invoice" || p.kind === "skip_collection"
                                                        ? "bg-amber-50 text-brand-amber"
                                                        : ""
                                                }`}
                                            >
                                                <span className="truncate">
                                                    {p.kind === "opening"
                                                        ? "🏦 Açılış"
                                                        : p.kind === "invoice"
                                                        ? "📄 Fatura"
                                                        : p.kind === "collection"
                                                        ? "💰 Tahsilat"
                                                        : p.kind === "skip_collection"
                                                        ? "⚠ Tahsilat atla"
                                                        : "⚠ Fatura atla"}
                                                    {" · "}{p.customer_code}
                                                    {p.invoice_no ? ` · ${p.invoice_no}` : ""}
                                                    {p.reference ? ` · ${p.reference}` : ""}
                                                </span>
                                                <span>₺ {(p.amount || 0).toFixed(2)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </details>
                            )}
                            {result.errors?.length > 0 && (
                                <div className="mt-3">
                                    <div className="overline text-brand-red">Hatalar</div>
                                    <ul className="text-xs font-mono text-brand-red mt-1 list-disc pl-5 space-y-1">
                                        {result.errors.map((e, i) => (
                                            <li key={i}>{e}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    <h3 className="font-display text-lg font-bold tracking-tight mb-3">
                        {mode === "auto" ? "Standart SOA Kolonları" : "Örnek Kolonlar"}
                    </h3>
                    <div className="text-[10px] font-mono text-neutral-600 space-y-1">
                        {mode === "auto" ? (
                            <ul className="list-disc pl-4 space-y-1">
                                <li>HESAP KODU · ADI · TARIH · TURU · FISNO</li>
                                <li>FATURANO · VADE · ACIKLAMA</li>
                                <li>BORC · ALACAK · BAKIYE BORC · BAKIYE ALACAK</li>
                                <li>Tarih formatı: 13.01.2026 · Tutar: 39.329,40</li>
                            </ul>
                        ) : (
                            <ul className="list-disc pl-4 space-y-1">
                                <li>Tarih · Belge No · Açıklama · Borç · Alacak</li>
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
